const BASE_URL = "https://medical-ruby.pixoatic.com";
const COUNTS_KEY = "chatUnreadCounts";
const CHATS_KEY = "chatFirebaseIds";
const EVENT_NAME = "chat-unread-changed";

const listeners: { [firebaseChatId: string]: () => void } = {};
const primed: { [firebaseChatId: string]: boolean } = {};
const accountByChat: { [firebaseChatId: string]: string } = {};
let watchStarted = false;

const readJson = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch (error) {
    return {};
  }
};

export const readUnreadCounts = () => readJson(COUNTS_KEY);

export const totalUnread = () =>
  Object.values(readUnreadCounts()).reduce(
    (sum: number, value) => sum + Number(value || 0),
    0
  );

const notify = () => {
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
};

export const clearUnread = (accountId: string) => {
  if (!accountId) return;
  const counts = readUnreadCounts();
  counts[String(accountId)] = 0;
  localStorage.setItem(COUNTS_KEY, JSON.stringify(counts));
  notify();
};

const bumpUnread = (accountId: string) => {
  if (!accountId) return;
  const counts = readUnreadCounts();
  const key = String(accountId);
  counts[key] = Number(counts[key] || 0) + 1;
  localStorage.setItem(COUNTS_KEY, JSON.stringify(counts));
  notify();
};

const rememberChat = (accountId: string, firebaseChatId: string) => {
  if (!accountId || !firebaseChatId) return;
  const chats = readJson(CHATS_KEY);
  chats[String(accountId)] = firebaseChatId;
  localStorage.setItem(CHATS_KEY, JSON.stringify(chats));
  accountByChat[firebaseChatId] = String(accountId);
};

const sameAccount = (left: string, right: string) => {
  const normalize = (value: string) => String(value || "").replace(/^account_/, "");
  return Boolean(normalize(left)) && normalize(left) === normalize(right);
};

const currentUserId = () =>
  String(
    localStorage.getItem("currentUserId") ||
      localStorage.getItem("userUniqnueId") ||
      ""
  );

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    token: token || "",
  };
};

const studyId = () => {
  const stored = localStorage.getItem("studyId");
  if (!stored) return "";
  try {
    return String(JSON.parse(stored));
  } catch (error) {
    return stored;
  }
};

const readFirebaseChatId = (payload: any) => {
  const layers = [payload, payload?.data, payload?.data?.attributes, payload?.chat];
  for (const layer of layers) {
    const value = layer?.firebase_chat_id || layer?.firebaseChatId;
    if (value) return String(value);
  }
  return "";
};

const listenToChat = async (firebaseChatId: string, accountId: string) => {
  if (!firebaseChatId || listeners[firebaseChatId]) return;
  rememberChat(accountId, firebaseChatId);
  const firebase = require("firebase/app");
  require("firebase/firestore");
  const unsubscribe = firebase
    .firestore()
    .collection("chats")
    .doc(firebaseChatId)
    .collection("messages")
    .onSnapshot(
      (snapshot: any) => {
        const ready = primed[firebaseChatId];
        primed[firebaseChatId] = true;
        if (!ready) return;
        const mine = currentUserId();
        const incoming = snapshot.docChanges().filter((change: any) => {
          if (change.type !== "added") return false;
          const data = change.doc.data() || {};
          const senderId = String(
            data.sender_id ?? data.senderId ?? data.account_id ?? data.user_id ?? ""
          );
          return !senderId || !sameAccount(senderId, mine);
        });
        incoming.forEach(() => bumpUnread(accountByChat[firebaseChatId] || accountId));
      },
      () => {
        const stop = listeners[firebaseChatId];
        if (stop) stop();
        delete listeners[firebaseChatId];
        delete primed[firebaseChatId];
      }
    );
  listeners[firebaseChatId] = unsubscribe;
};

const signInAndWatch = async (chats: { accountId: string; firebaseChatId: string }[]) => {
  const tokenResponse = await fetch(`${BASE_URL}/bx_block_chat/firebase/token`, {
    headers: authHeaders(),
  });
  const tokenJson = await tokenResponse.json();
  const customToken = tokenJson?.data?.token || tokenJson?.token;
  if (!customToken) return;
  const firebase = require("firebase/app");
  require("firebase/auth");
  require("firebase/firestore");
  if (!firebase.apps.length) {
    firebase.initializeApp({
      apiKey: "AIzaSyBe3YSP0FdEknT9s5RA2dmLT_9563egUKI",
      authDomain: "medical-2f3fc.firebaseapp.com",
      projectId: "medical-2f3fc",
      storageBucket: "medical-2f3fc.firebasestorage.app",
      messagingSenderId: "286877870670",
      appId: "1:286877870670:web:e28ff5696f6ecaaf5cf11f",
      measurementId: "G-2W8F4D7M6L",
    });
  }
  if (!firebase.auth().currentUser) {
    await firebase.auth().signInWithCustomToken(customToken);
  }
  await Promise.all(
    chats.map((chat) => listenToChat(chat.firebaseChatId, chat.accountId))
  );
};

export const watchPatientChat = (accountId: string, firebaseChatId: string) => {
  if (!accountId || !firebaseChatId) return;
  void listenToChat(firebaseChatId, accountId);
};

export const startChatUnreadWatch = () => {
  if (watchStarted) return;
  const token = localStorage.getItem("token");
  const activeStudyId = studyId();
  if (!token || !activeStudyId) return;
  watchStarted = true;

  void (async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/bx_block_catalogue/studies/studies/${activeStudyId}/active_study_patients`,
        { headers: authHeaders() }
      );
      const payload = await response.json();
      const records = payload?.patients || payload?.data?.patients || [];
      const saved = readJson(CHATS_KEY);
      const chats: { accountId: string; firebaseChatId: string }[] = [];

      for (const record of records) {
        const accountId = String(record?.patient_id || record?.account_id || "");
        if (!accountId) continue;
        let firebaseChatId = saved[accountId] || "";
        if (!firebaseChatId) {
          const created = await fetch(
            `${BASE_URL}/bx_block_blogpostsmanagement/community_chats/create_chat`,
            {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({ chat_account_id: Number(accountId) }),
            }
          );
          const createdJson = await created.json();
          firebaseChatId = readFirebaseChatId(createdJson);
        }
        if (firebaseChatId) chats.push({ accountId, firebaseChatId });
      }

      Object.keys(saved).forEach((accountId) => {
        if (!chats.some((chat) => chat.accountId === accountId) && saved[accountId]) {
          chats.push({ accountId, firebaseChatId: saved[accountId] });
        }
      });

      await signInAndWatch(chats);
    } catch (error) {
      watchStarted = false;
    }
  })();
};

export const CHAT_UNREAD_EVENT = EVENT_NAME;
